/**
 * Layout utilities — the classes that make primitives composable.
 *
 * The tension this resolves: the export guarantee says a block may only reference classes in
 * the registry, which is what stops generated pages carrying styles Webflow has never seen.
 * But a modular builder needs per-element layout control. Free-form CSS would break the
 * guarantee; a fixed set of template blocks is what the user just told us is too rigid.
 *
 * The answer is a generated utility set derived from the design scale. Structure is
 * completely free — any nesting, any arrangement — but every *value* snaps to a token. You
 * cannot produce 23px of padding or an off-brand pink, and every class is known to the
 * exporter.
 *
 * All of these are combo classes of a single base, `lp-box`, because Webflow combos attach to
 * exactly one base class. An element ends up as `lp-box is-flex is-row is-gap-5`, which is the
 * same stacking Webflow's own Designer produces.
 *
 * ## On responsiveness
 *
 * Elementor makes every layout property overridable at every breakpoint. Done literally that
 * multiplies this file by seven. Instead each utility carries sensible breakpoint behaviour
 * *inside* the class — `is-cols-4` drops to two columns on tablet and one on mobile, `is-row`
 * can be paired with `is-stack-sm` — which covers the layouts a marketing page actually needs.
 * If a real case needs more, add the specific variant rather than the whole matrix.
 */

import { colour, layout, radiusScale, shadow, space, tones, type SpaceStep, type ToneName } from '../tokens.js';
import { BOX_RESET, type StyleDef } from '../styleDef.js';

const combo = (name: string, base: StyleDef['base'], variants?: StyleDef['variants']): StyleDef => ({
  name,
  comb: true,
  parent: 'lp-box',
  base,
  ...(variants ? { variants } : {}),
});

/**
 * The one base every layout element carries.
 *
 * `min-width: 0` is the important line. A grid or flex child defaults to `min-width: auto`,
 * which refuses to shrink below its content's minimum width — so a long heading or a wide
 * image pushes straight out of its column instead of wrapping. Every box is a potential
 * flex/grid child, so it belongs here rather than being remembered case by case.
 */
export const lpBox: StyleDef = {
  name: 'lp-box',
  base: {
    ...BOX_RESET,
    display: 'block',
    position: 'relative',
    'min-width': '0',
    'max-width': '100%',
  },
};

/* ---------------------------------------------------------------- *
 * Display
 * ---------------------------------------------------------------- */

export const displayUtils: StyleDef[] = [
  combo('is-flex', {
    display: 'flex',
    'flex-direction': 'column',
    'align-items': 'stretch',
    'justify-content': 'flex-start',
  }),
  combo('is-grid', { display: 'grid', 'grid-auto-flow': 'row', 'align-items': 'stretch' }),
  combo('is-inline-flex', { display: 'inline-flex', 'align-items': 'center' }),
  combo('is-hidden', { display: 'none' }),
  // Visibility per breakpoint: a real need on marketing pages (swap a desktop table for a
  // mobile card list), and cheaper than making every property responsive.
  combo('is-hide-mobile', {}, { small: { display: 'none' } }),
  combo('is-hide-desktop', { display: 'none' }, { small: { display: 'block' } }),
];

/* ---------------------------------------------------------------- *
 * Flex direction, wrapping and alignment
 * ---------------------------------------------------------------- */

export const flexUtils: StyleDef[] = [
  combo('is-row', { 'flex-direction': 'row', 'align-items': 'center' }),
  combo('is-col', { 'flex-direction': 'column', 'align-items': 'stretch' }),
  /**
   * Visually flips a row without touching the DOM order.
   *
   * This is how "image on the left" works. Reordering the markup instead would put the image
   * before the heading in the source, which is what a screen reader announces first and what
   * Google reads first — so the copy stays first and only the paint order changes.
   */
  combo('is-row-reverse', { 'flex-direction': 'row-reverse', 'align-items': 'center' }),
  /** Pair with `is-row`: horizontal on desktop, stacked on mobile. The commonest need. */
  combo('is-stack-sm', {}, { small: { 'flex-direction': 'column', 'align-items': 'stretch' } }),
  combo('is-stack-md', {}, { medium: { 'flex-direction': 'column', 'align-items': 'stretch' } }),
  combo('is-wrap', { 'flex-wrap': 'wrap' }),
  combo('is-nowrap', { 'flex-wrap': 'nowrap' }),
  combo('is-grow', { 'flex-grow': '1' }),

  combo('is-justify-start', { 'justify-content': 'flex-start' }),
  combo('is-justify-center', { 'justify-content': 'center' }),
  combo('is-justify-end', { 'justify-content': 'flex-end' }),
  combo('is-justify-between', { 'justify-content': 'space-between' }),
  combo('is-justify-around', { 'justify-content': 'space-around' }),

  combo('is-align-start', { 'align-items': 'flex-start' }),
  combo('is-align-center', { 'align-items': 'center' }),
  combo('is-align-end', { 'align-items': 'flex-end' }),
  combo('is-align-stretch', { 'align-items': 'stretch' }),
  combo('is-align-baseline', { 'align-items': 'baseline' }),
];

/* ---------------------------------------------------------------- *
 * Spacing — gap and padding, both off the 4px scale
 * ---------------------------------------------------------------- */

const SPACE_STEPS: SpaceStep[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** Section padding shrinks on smaller screens, which is almost always what you want. */
function responsivePad(step: SpaceStep): StyleDef['variants'] | undefined {
  if (step < 7) return undefined;
  const medium = space[(step - 1) as SpaceStep];
  const small = space[(step - 2) as SpaceStep];
  return {
    medium: { 'padding-top': medium, 'padding-bottom': medium },
    small: { 'padding-top': small, 'padding-bottom': small },
  };
}

export const spacingUtils: StyleDef[] = [
  ...SPACE_STEPS.map((s) =>
    combo(`is-gap-${s}`, { 'grid-row-gap': space[s], 'grid-column-gap': space[s] }),
  ),
  ...SPACE_STEPS.map((s) =>
    combo(`is-pad-y-${s}`, { 'padding-top': space[s], 'padding-bottom': space[s] }, responsivePad(s)),
  ),
  ...SPACE_STEPS.map((s) =>
    combo(`is-pad-x-${s}`, { 'padding-left': space[s], 'padding-right': space[s] }),
  ),
  ...SPACE_STEPS.map((s) =>
    combo(`is-pad-${s}`, {
      'padding-top': space[s],
      'padding-right': space[s],
      'padding-bottom': space[s],
      'padding-left': space[s],
    }),
  ),
];

/* ---------------------------------------------------------------- *
 * Grid columns
 * ---------------------------------------------------------------- */

/**
 * Column counts collapse sensibly rather than requiring per-breakpoint configuration:
 * four columns are too dense on tablet, and anything above one is too dense on a phone.
 */
function columnVariants(n: number): StyleDef['variants'] {
  const tablet = n >= 4 ? 2 : Math.min(n, 2);
  return {
    medium: { 'grid-template-columns': `repeat(${tablet}, minmax(0, 1fr))` },
    small: { 'grid-template-columns': 'minmax(0, 1fr)' },
  };
}

export const columnUtils: StyleDef[] = [1, 2, 3, 4, 5, 6].map((n) =>
  combo(
    `is-cols-${n}`,
    { 'grid-template-columns': `repeat(${n}, minmax(0, 1fr))` },
    n > 1 ? columnVariants(n) : undefined,
  ),
);

/**
 * Named splits for the Columns block.
 *
 * `1-2` reads as the track ratio, so `is-split-2-1` is a wide left column beside a narrow
 * right one. All collapse to a single column on mobile.
 */
export const splitUtils: StyleDef[] = (
  [
    ['1-1', 'minmax(0, 1fr) minmax(0, 1fr)'],
    ['2-1', 'minmax(0, 2fr) minmax(0, 1fr)'],
    ['1-2', 'minmax(0, 1fr) minmax(0, 2fr)'],
    ['1-1-1', 'repeat(3, minmax(0, 1fr))'],
    ['1-1-1-1', 'repeat(4, minmax(0, 1fr))'],
  ] as const
).map(([name, tracks]) =>
  combo(
    `is-split-${name}`,
    { 'grid-template-columns': tracks },
    {
      // Four columns are too dense on a tablet; everything goes single-column on a phone.
      ...(name === '1-1-1-1'
        ? { medium: { 'grid-template-columns': 'repeat(2, minmax(0, 1fr))' } }
        : {}),
      small: { 'grid-template-columns': 'minmax(0, 1fr)' },
    },
  ),
);

/** Puts the last column first once the split has stacked. */
export const reverseUtils: StyleDef[] = [
  combo('is-reverse-sm', {}, { small: { 'grid-auto-flow': 'dense', 'flex-direction': 'column-reverse' } }),
];

/** Let a child span more than one grid track — the "wide first card" pattern. */
export const spanUtils: StyleDef[] = [2, 3].map((n) =>
  combo(
    `is-span-${n}`,
    { 'grid-column-start': 'span 1', 'grid-column-end': `span ${n}` },
    { small: { 'grid-column-end': 'span 1' } },
  ),
);

/* ---------------------------------------------------------------- *
 * Width
 * ---------------------------------------------------------------- */

export const widthUtils: StyleDef[] = [
  combo('is-w-narrow', { width: '100%', 'max-width': layout.containerNarrow, 'margin-left': 'auto', 'margin-right': 'auto' }),
  combo('is-w-normal', { width: '100%', 'max-width': layout.containerMax, 'margin-left': 'auto', 'margin-right': 'auto' }),
  combo('is-w-wide', { width: '100%', 'max-width': '1400px', 'margin-left': 'auto', 'margin-right': 'auto' }),
  combo('is-w-full', { width: '100%', 'max-width': 'none' }),
  combo('is-w-auto', { width: 'auto' }),
  combo('is-w-half', { width: '100%', 'flex-basis': '50%', 'flex-grow': '0' }, { small: { 'flex-basis': '100%' } }),
  combo('is-w-third', { width: '100%', 'flex-basis': '33.333%', 'flex-grow': '0' }, { small: { 'flex-basis': '100%' } }),
  combo('is-w-two-thirds', { width: '100%', 'flex-basis': '66.666%', 'flex-grow': '0' }, { small: { 'flex-basis': '100%' } }),
];

/* ---------------------------------------------------------------- *
 * Surface — background, radius, border, shadow
 * ---------------------------------------------------------------- */

export const toneUtils: StyleDef[] = (Object.keys(tones) as ToneName[]).map((name) =>
  combo(`is-bg-${name}`, { 'background-color': tones[name].bg }),
);

export const surfaceUtils: StyleDef[] = [
  combo('is-bg-yellow', { 'background-color': colour.yellow }),
  combo('is-bg-teal', { 'background-color': colour.teal }),

  ...(Object.entries(radiusScale) as [keyof typeof radiusScale, string][]).map(([key, value]) =>
    combo(`is-radius-${key}`, {
      'border-top-left-radius': value,
      'border-top-right-radius': value,
      'border-bottom-left-radius': value,
      'border-bottom-right-radius': value,
    }),
  ),

  combo('is-border', {
    'border-top-width': '1px',
    'border-top-style': 'solid',
    'border-top-color': colour.border,
    'border-right-width': '1px',
    'border-right-style': 'solid',
    'border-right-color': colour.border,
    'border-bottom-width': '1px',
    'border-bottom-style': 'solid',
    'border-bottom-color': colour.border,
    'border-left-width': '1px',
    'border-left-style': 'solid',
    'border-left-color': colour.border,
  }),

  combo('is-shadow-card', { 'box-shadow': shadow.card }),
  combo('is-shadow-lifted', { 'box-shadow': shadow.lifted }),

  /** The frosted panel from every /lp/ "how it works" band: 80% purple behind a 5px blur. */
  combo('is-glass', { 'background-color': colour.indigoGlass, filter: 'none' }),

  /**
   * A grid cell that is allowed to stretch to its row.
   *
   * This exists to undo Puck. Its `.DropZone { height: 100% }` rule lands on every slot, and
   * `height: 100%` is not `auto` — so per CSS box alignment the grid's `align-items: stretch`
   * stops applying to it, and because a grid row's height is indefinite the percentage
   * resolves back to content height anyway. The column ends up exactly as tall as its
   * contents, which leaves `justify-content` no free space to distribute and makes every
   * vertical alignment setting silently do nothing.
   *
   * Restoring `height: auto` lets the grid stretch the cell again. `.lp-box.is-cell` is two
   * classes to Puck's one, so it wins on specificity without an `!important`.
   *
   * Harmless in the export — Webflow has no such rule, and this is what a cell does anyway.
   */
  combo('is-cell', { height: 'auto', 'align-self': 'stretch' }),

  combo('is-fill-height', { height: '100%' }),
  combo('is-minh-sm', { 'min-height': '240px' }),
  combo('is-minh-md', { 'min-height': '420px' }),
  combo('is-minh-lg', { 'min-height': '620px' }),

  combo('is-overflow-hidden', { 'overflow-x': 'hidden', 'overflow-y': 'hidden' }),
];

/* ---------------------------------------------------------------- *
 * Text alignment on containers — inherited by the text inside
 * ---------------------------------------------------------------- */

export const textAlignUtils: StyleDef[] = [
  combo('is-text-left', { 'text-align': 'left' }),
  combo('is-text-center', { 'text-align': 'center' }),
  combo('is-text-right', { 'text-align': 'right' }),
];

export const utilityStyles: StyleDef[] = [
  lpBox,
  ...displayUtils,
  ...flexUtils,
  ...spacingUtils,
  ...columnUtils,
  ...splitUtils,
  ...reverseUtils,
  ...spanUtils,
  ...widthUtils,
  ...toneUtils,
  ...surfaceUtils,
  ...textAlignUtils,
];
